import React, { useState, useRef, useEffect } from 'react';
import styles from './FormField.module.scss';

interface BaseFieldProps {
  label?: string;
  error?: string;
  required?: boolean;
  hint?: string;
  className?: string;
}

interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectWithAddProps extends BaseFieldProps {
  value: string | number;
  onChange: (value: string) => void;
  options: SelectOption[];
  onAddNew?: (newValue: string) => void; // Optional callback when new item is added
  placeholder?: string;
  disabled?: boolean;
  allowAdd?: boolean; // Enable/disable add functionality
  id?: string;
  addNewLabel?: string; // Custom label for "Add New" option
  newItemPlaceholder?: string; // Placeholder for the input field
}

const ADD_NEW_VALUE = '__ADD_NEW__';

export const SelectWithAdd: React.FC<SelectWithAddProps> = ({
  label,
  value,
  onChange,
  options,
  onAddNew,
  placeholder,
  error,
  required,
  hint,
  disabled,
  allowAdd = true,
  id,
  className = '',
  addNewLabel = '+ Add New',
  newItemPlaceholder = 'Enter new value',
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

  // Focus input when entering add mode
  useEffect(() => {
    if (isAddingNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingNew]);

  // Handle select change
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === ADD_NEW_VALUE) {
      setIsAddingNew(true);
      setNewValue('');
      setValidationError(null);
    } else {
      onChange(selectedValue);
    }
  };

  // Handle adding new value
  const handleAddNew = () => {
    const trimmedValue = newValue.trim();
    
    // Validation
    if (!trimmedValue) {
      setValidationError('Value cannot be empty');
      return;
    }

    // Check for duplicates (case-insensitive)
    const existingValues = options.map(opt => 
      String(opt.value).toLowerCase()
    );
    if (existingValues.includes(trimmedValue.toLowerCase())) {
      setValidationError('This value already exists');
      return;
    }

    // Clear validation error
    setValidationError(null);

    // Add new value and select it
    onChange(trimmedValue);
    
    // Call callback if provided
    if (onAddNew) {
      onAddNew(trimmedValue);
    }

    // Reset add mode
    setIsAddingNew(false);
    setNewValue('');
  };

  // Handle cancel
  const handleCancel = () => {
    setIsAddingNew(false);
    setNewValue('');
    setValidationError(null);
  };

  // Handle Enter key in input
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNew();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Combine options with "Add New" option if allowed
  const allOptions = allowAdd 
    ? [...options, { value: ADD_NEW_VALUE, label: addNewLabel }]
    : options;

  // Determine display error
  const displayError = validationError || error;

  return (
    <div className={`${styles.field} ${className}`}>
      {label && (
        <label htmlFor={selectId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      
      {!isAddingNew ? (
        <select
          id={selectId}
          value={value}
          onChange={handleSelectChange}
          disabled={disabled}
          className={`${styles.select} ${displayError ? styles.hasError : ''}`}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {allOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div className={styles.addNewContainer}>
          <input
            ref={inputRef}
            type="text"
            value={newValue}
            onChange={(e) => {
              setNewValue(e.target.value);
              setValidationError(null);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={newItemPlaceholder}
            disabled={disabled}
            className={`${styles.input} ${displayError ? styles.hasError : ''}`}
          />
          <div className={styles.addNewActions}>
            <button
              type="button"
              onClick={handleAddNew}
              disabled={disabled}
              className={styles.addButton}
            >
              Add
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={disabled}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {hint && !displayError && <span className={styles.hint}>{hint}</span>}
      {displayError && <span className={styles.error}>{displayError}</span>}
    </div>
  );
};
